import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/shared/lib/supabase";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useIsAdmin } from "@/shared/hooks/useIsAdmin";
import { ADMIN_ROUTES } from "@/shared/constants/paths";

const SIMULATION_KEY = "admin_simulation";
const SIMULATION_MAX_DURATION_MS = 60 * 60 * 1000; // 1 hour

export interface SimulationState {
  originalPersonId: number | null;
  targetPersonId: number;
  targetPersonName: string;
  adminUserId: number;
  startedAt: number;
  createdLink: boolean; // Whether we created the user_estates link (vs it already existed)
}

export function useAdminSimulation() {
  const { user, refetch } = useAppContext();
  const { isAdmin } = useIsAdmin();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Read simulation state from localStorage
  const [simulationState, setSimulationState] = useState<SimulationState | null>(() => {
    try {
      const stored = localStorage.getItem(SIMULATION_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const isSimulating = !!simulationState;

  // Check for stale simulations or mismatched user on mount/user change
  useEffect(() => {
    if (simulationState) {
      // Clear simulation if no user is logged in
      if (!user) {
        console.warn("[AdminSimulation] Clearing simulation - user logged out");
        localStorage.removeItem(SIMULATION_KEY);
        setSimulationState(null);
        return;
      }

      // Clear simulation if logged-in user doesn't match the admin who started it
      if (simulationState.adminUserId !== user.user_id) {
        console.warn("[AdminSimulation] Clearing simulation - different user logged in");
        localStorage.removeItem(SIMULATION_KEY);
        setSimulationState(null);
        return;
      }

      // Auto-cleanup stale simulation (older than 1 hour)
      const elapsed = Date.now() - simulationState.startedAt;
      if (elapsed > SIMULATION_MAX_DURATION_MS) {
        console.warn("[AdminSimulation] Cleaning up stale simulation");
        endSimulation();
      }
    }
  }, [user?.user_id]); // Run on mount and when user changes

  // Check if user is allowed to simulate (admins only)
  const canSimulate = isAdmin;

  const startSimulation = useCallback(
    async (estateId: number, personName: string) => {
      if (!canSimulate || !user) {
        return { success: false, error: "Not authorized to simulate" };
      }

      // If already simulating, clean up the previous simulation first
      if (isSimulating && simulationState) {
        try {
          // Remove the user_estates link if we created it
          if (simulationState.createdLink) {
            await supabase
              .from("user_estates")
              .delete()
              .eq("user_id", simulationState.adminUserId)
              .eq("estate_id", simulationState.targetPersonId);
          }

          // Log simulation end
          await supabase.from("action_logs").insert({
            estate_id: simulationState.targetPersonId,
            created_by_admin: true,
            action_type: "admin_simulation_end",
            action_details: {
              admin_user_id: simulationState.adminUserId,
              duration_ms: Date.now() - simulationState.startedAt,
              reason: "switched_to_new_simulation",
            },
          });

          localStorage.removeItem(SIMULATION_KEY);
        } catch (cleanupError) {
          console.warn("[AdminSimulation] Error cleaning up previous simulation:", cleanupError);
        }
      }

      try {
        // Check if admin already has access to this person
        const { data: existingLink } = await supabase
          .from("user_estates")
          .select("*")
          .eq("user_id", user.user_id)
          .eq("estate_id", estateId)
          .maybeSingle();

        const createdLink = !existingLink;

        // If no existing link, create one
        if (createdLink) {
          const { error: linkError } = await supabase
            .from("user_estates")
            .insert({ user_id: user.user_id, estate_id: estateId });

          if (linkError) {
            console.error("[AdminSimulation] Failed to create user_estates link:", linkError);
            return { success: false, error: linkError.message };
          }
        }

        // Store current state for restoration
        const state: SimulationState = {
          originalPersonId: user.last_active_estate_id,
          targetPersonId: estateId,
          targetPersonName: personName,
          adminUserId: user.user_id,
          startedAt: Date.now(),
          createdLink,
        };

        // Update admin's last_active_estate_id to target person
        const { error: updateError } = await supabase
          .from("users")
          .update({ last_active_estate_id: estateId })
          .eq("user_id", user.user_id);

        if (updateError) {
          // Rollback: remove the link we created
          if (createdLink) {
            await supabase
              .from("user_estates")
              .delete()
              .eq("user_id", user.user_id)
              .eq("estate_id", estateId);
          }
          console.error("[AdminSimulation] Failed to update last_active_estate_id:", updateError);
          return { success: false, error: updateError.message };
        }

        // Log simulation start to action_logs
        await supabase.from("action_logs").insert({
          estate_id: estateId,
          created_by_admin: true,
          action_type: "admin_simulation_start",
          action_details: {
            admin_user_id: user.user_id,
            admin_email: user.email,
          },
        });

        // Save state to localStorage
        localStorage.setItem(SIMULATION_KEY, JSON.stringify(state));
        setSimulationState(state);

        // Invalidate all queries to force refetch with new person context
        queryClient.invalidateQueries();

        // Refresh the app context
        refetch();

        return { success: true };
      } catch (error) {
        console.error("[AdminSimulation] Error starting simulation:", error);
        return { success: false, error: "Failed to start simulation" };
      }
    },
    [canSimulate, user, isSimulating, simulationState, queryClient, refetch],
  );

  const endSimulation = useCallback(async () => {
    const state = simulationState;
    if (!state) return;

    try {
      // Remove the user_estates link if we created it
      if (state.createdLink) {
        await supabase
          .from("user_estates")
          .delete()
          .eq("user_id", state.adminUserId)
          .eq("estate_id", state.targetPersonId);
      }

      // Restore original last_active_estate_id
      if (state.originalPersonId !== null) {
        await supabase
          .from("users")
          .update({ last_active_estate_id: state.originalPersonId })
          .eq("user_id", state.adminUserId);
      }

      // Log simulation end
      await supabase.from("action_logs").insert({
        estate_id: state.targetPersonId,
        created_by_admin: true,
        action_type: "admin_simulation_end",
        action_details: {
          admin_user_id: state.adminUserId,
          duration_ms: Date.now() - state.startedAt,
        },
      });

      // Clear localStorage
      localStorage.removeItem(SIMULATION_KEY);
      setSimulationState(null);

      // Invalidate all queries
      queryClient.invalidateQueries();

      // Navigate back to the decedent's profile
      navigate(ADMIN_ROUTES.LAWYERS);
    } catch (error) {
      console.error("[AdminSimulation] Error ending simulation:", error);
      // Still clear state even if cleanup fails
      localStorage.removeItem(SIMULATION_KEY);
      setSimulationState(null);
      navigate(ADMIN_ROUTES.LAWYERS);
    }
  }, [simulationState, queryClient, navigate]);

  return {
    isSimulating,
    simulationState,
    canSimulate,
    startSimulation,
    endSimulation,
  };
}
